import React, { useEffect, useState } from "react";
import { Button, Card, Typography, Segmented, Spin, Alert } from "antd";
import { useNavigate } from "react-router-dom";
import { db, auth } from "./FirebaseConfig";
import {
  doc,
  setDoc,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";

const { Title } = Typography;

/* =========================
   規約バージョン
   ※ 規約更新時にインクリメント
========================= */
const AGREEMENT_VERSION = 1;

export default function TermsPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(setUser);
    return () => unsub();
  }, []);
  /* =========================
     UI state
  ========================= */
  const [lang, setLang] = useState("jp"); // jp | en
  const [docType, setDocType] = useState("terms"); // terms | privacy
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);

  /* =========================
     同意状態
  ========================= */
  const [agreed, setAgreed] = useState(false);

  /* =========================
     Firestore から同意状態監視
  ========================= */
  useEffect(() => {
    if (!user) return;

    const ref = doc(db, "users", user.uid);
    return onSnapshot(ref, (snap) => {
      const d = snap.data();
      const ok =
        d?.agreeTerms === true &&
        d?.agreePrivacy === true &&
        d?.agreeVersion === AGREEMENT_VERSION;
      setAgreed(ok);
    });
  }, [user]);

  /* =========================
     規約HTMLロード
  ========================= */
  useEffect(() => {
    const loadDocument = async () => {
      setLoading(true);

      const fileMap = {
        jp: {
          terms: "/legal/StomataScope_Terms_JP.html",
          privacy: "/legal/PrivacyPolicy_JP.html",
        },
        en: {
          terms: "/legal/StomataScope_Terms_EN.html",
          privacy: "/legal/PrivacyPolicy_EN.html",
        },
      };

      try {
        const res = await fetch(fileMap[lang][docType]);
        const text = await res.text();
        setContent(text);
      } catch (e) {
        console.error(e);
        setContent("<p>Failed to load document.</p>");
      } finally {
        setLoading(false);
      }
    };

    loadDocument();
  }, [lang, docType]);

  /* =========================
     ラベル定義
  ========================= */
  const DOC_LABELS = {
    jp: {
      terms: "利用規約",
      privacy: "プライバシーポリシー",
    },
    en: {
      terms: "Terms of Service",
      privacy: "Privacy Policy",
    },
  };

  const PAGE_TITLES = {
    jp: "利用規約・プライバシーポリシー",
    en: "Terms of Service & Privacy Policy",
  };

  /* =========================
     同意処理（★重要）
  ========================= */
  const handleAgree = async () => {
    if (!user) return;

    await setDoc(
      doc(db, "users", user.uid),
      {
        agreeTerms: true,
        agreePrivacy: true,
        agreeVersion: AGREEMENT_VERSION,
        agreeAt: serverTimestamp(),
        agreeLang: lang,

        // 🔥 AuthGate を通すために必須
        lastLoginAt: serverTimestamp(),
      },
      { merge: true }
    );

    // 🔥 同意後は必ずアプリへ戻す
    navigate("/", { replace: true });
  };

  /* =========================
     render
  ========================= */
  return (
    <div
      style={{
        minHeight: "100vh",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundImage: `
      radial-gradient(
        circle at top right,
        rgba(255, 242, 0, 0.18),
        rgba(255, 242, 0, 0.04) 40%,
        rgba(255, 255, 255, 0) 70%
      ),
      url(/main-2048x972.png)
    `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Card style={{ maxWidth: 900, margin: "32px auto" }}>
        <Title level={3}>{PAGE_TITLES[lang]}</Title>

        {/* 言語切替 */}
        <Segmented
          options={[
            { label: "日本語", value: "jp" },
            { label: "English", value: "en" },
          ]}
          value={lang}
          onChange={setLang}
          style={{ marginBottom: 16 }}
        />

        {/* 規約 / プライバシー切替 */}
        <Segmented
          options={[
            { label: DOC_LABELS[lang].terms, value: "terms" },
            { label: DOC_LABELS[lang].privacy, value: "privacy" },
          ]}
          value={docType}
          onChange={setDocType}
          style={{ marginBottom: 16 }}
        />

        {/* 本文 */}
        <div
          style={{
            border: "1px solid #eee",
            padding: 16,
            height: 400,
            overflowY: "auto",
            marginBottom: 24,
            background: "#fafafa",
          }}
        >
          {loading ? (
            <Spin />
          ) : (
            <div dangerouslySetInnerHTML={{ __html: content }} />
          )}
        </div>

        {agreed ? (
          <Button block onClick={() => navigate("/", { replace: true })}>
            {lang === "jp" ? "ホームへ戻る" : "Go to Home"}
          </Button>
        ) : (
          <Button type="primary" block onClick={handleAgree}>
            {lang === "jp" ? "同意して進む" : "Agree and continue"}
          </Button>
        )}
      </Card>

    </div>
  );
}
